/**
 * Search Counts Fetcher
 *
 * Server-side function to fetch result counts for all search tabs.
 * Calls the internal batch search directly (no HTTP round-trip).
 *
 * Previously this went through the /api/products/batch-search endpoint
 * which caused unnecessary server-to-self HTTP round-trips on Vercel.
 *
 * Billing impact:
 * - 1 Search Request (single Algolia API call)
 * - 3 Search Operations (Watch, Strap, Eyewear)
 */

import { batchSearchProducts } from '@/lib/algolia/search';
import { devLog } from '@/lib/utils/devLog';

/**
 * Search counts for all tabs
 */
export interface SearchCounts {
  all: number;
  watches: number;
  straps: number;
  eyewear: number;
  stores: number;
}

/**
 * Fetch result counts for all search tabs using BATCHED search.
 *
 * Calls batchSearchProducts directly which combines all queries
 * into a single Algolia API call, reducing quota usage from 3 requests to 1.
 *
 * @param locale - Locale string (e.g., 'us-en')
 * @param query - Search query string
 * @returns Counts for each tab
 */
export async function fetchSearchCounts(locale: string, query: string): Promise<SearchCounts> {
  if (!query.trim()) {
    return {
      all: 0,
      watches: 0,
      straps: 0,
      eyewear: 0,
      stores: 0,
    };
  }

  devLog('[fetchSearchCounts] Fetching counts via batch search:', { locale, query });

  try {
    const results = await batchSearchProducts({
      locale,
      queries: [
        { productType: 'Watch', query, hitsPerPage: 0 },
        { productType: 'Strap', query, hitsPerPage: 0 },
        { productType: 'Eyewear', query, hitsPerPage: 0 },
      ],
    });

    const countMap: Record<string, number> = {};
    for (const result of results) {
      countMap[result.productType] = result.totalHits;
    }

    const watches = countMap['Watch'] || 0;
    const straps = countMap['Strap'] || 0;
    const eyewear = countMap['Eyewear'] || 0;

    // TODO: Fetch store counts when store search is implemented
    const storeCount = 0;

    devLog('[fetchSearchCounts] Counts:', { watches, straps, eyewear, stores: storeCount });

    return {
      all: watches + straps + eyewear + storeCount,
      watches,
      straps,
      eyewear,
      stores: storeCount,
    };
  } catch (error) {
    devLog('[fetchSearchCounts] Error, falling back to zeros:', error);
    return {
      all: 0,
      watches: 0,
      straps: 0,
      eyewear: 0,
      stores: 0,
    };
  }
}
